import { FC, ReactNode } from 'react';
import { AlertCircle, Inbox } from 'lucide-react';
import {
  ActionMenuItemConfig,
  DropdownButton,
} from 'components/navigation/Dropdown/GenericDropdownButton';
import { GenericLoading } from 'components/ui/Loader';

interface BlockCardProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Entrées du menu « Actions » du cartouche. */
  actions?: ActionMenuItemConfig[];
  /** Contenu libre à droite du titre (filtre, compteur…). */
  headerExtra?: ReactNode;
  isLoading?: boolean;
  error?: string | null;
  isEmpty?: boolean;
  emptyLabel?: string;
  /** Retire le rembourrage du corps pour les listes qui gèrent le leur. */
  flushBody?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Cartouche de la page tunnel : bandeau de titre coloré, menu « Actions », et gestion
 * homogène des états de chargement / erreur / vide.
 *
 * Le bandeau teinté remplace le titre sur fond blanc de l'ancien écran : sur une page
 * qui empile huit blocs, rien ne signalait où l'un finissait et où le suivant commençait.
 */
export const BlockCard: FC<BlockCardProps> = ({
  title,
  description,
  icon,
  actions,
  headerExtra,
  isLoading,
  error,
  isEmpty,
  emptyLabel = 'Aucun élément',
  flushBody,
  className = '',
  children,
}) => (
  <section
    className={`overflow-hidden rounded-2xl bg-white shadow-xl shadow-zinc-900/10 ${className}`}
  >
    <header className="flex flex-wrap items-center justify-between gap-3 bg-primary px-5 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        {icon && <span className="flex-shrink-0 text-white/80">{icon}</span>}
        <div className="min-w-0">
          <h2 className="truncate text-base font-bold text-white">{title}</h2>
          {description && <p className="truncate text-xs text-white/70">{description}</p>}
        </div>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        {headerExtra}
        {actions && actions.length > 0 && (
          <DropdownButton
            items={actions}
            anchor="bottom"
            trigger={
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-white/15 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/25"
              >
                <span aria-hidden>⚙</span>
                Actions
                <span aria-hidden className="text-xs">▾</span>
              </button>
            }
          />
        )}
      </div>
    </header>

    <div className={flushBody ? '' : 'px-5 py-4'}>
      {isLoading ? (
        <div className="py-6">
          <GenericLoading />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center gap-2 py-8 text-red-500">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">{error}</span>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-gray-400">
          <Inbox className="h-7 w-7" />
          <span className="text-sm">{emptyLabel}</span>
        </div>
      ) : (
        children
      )}
    </div>
  </section>
);

/**
 * Liste à lignes alternées et survol marqué.
 *
 * L'alternance et le survol sont portés par le conteneur plutôt que répétés sur chaque
 * ligne : tous les blocs de la page se comportent ainsi de la même façon, sans que
 * chaque liste ait à redéfinir ses classes.
 */
export const StripedList: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <ul
    className={`[&>li]:border-b [&>li]:border-gray-100 [&>li:last-child]:border-0 [&>li:nth-child(even)]:bg-primary/[0.07] [&>li]:transition-colors [&>li:hover]:bg-primary/15 ${className}`}
  >
    {children}
  </ul>
);

/**
 * Colonne de lignes libellé / valeur, alternées.
 *
 * L'alternance est portée par le conteneur : chaque colonne du bloc Détail garde ainsi
 * son propre rythme, ce qu'un décompte global casserait dès que les deux colonnes n'ont
 * pas le même nombre de lignes. Le survol est le même que celui des listes : sur une
 * page qui empile huit blocs, une ligne qui réagit ici et pas là se lit comme une
 * différence de nature alors qu'il n'y en a pas.
 */
export const BlockRows: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="[&>div:nth-child(even)]:bg-primary/[0.07] [&>div]:transition-colors [&>div:hover]:bg-primary/15">
    {children}
  </div>
);

/** Ligne libellé / valeur alignée à droite, pour les blocs de consultation. */
export const BlockRow: FC<{ label: string; children: ReactNode; hint?: string }> = ({
  label,
  children,
  hint,
}) => (
  <div className="flex items-baseline justify-between gap-6 rounded-md px-3 py-1.5">
    <span className="text-sm font-semibold text-zinc-700">
      {label}
      {hint && (
        <span className="ml-1 cursor-help font-normal text-gray-300" title={hint} aria-label={hint}>
          ⓘ
        </span>
      )}
    </span>
    <span className="text-right text-sm text-gray-600">{children}</span>
  </div>
);
